import isNil from 'lodash/isNil';

const TOKEN_FALLBACK_TTL_MS = 60_000;
const TOKEN_ERROR_TTL_MS = 5_000;
const TOKEN_EXPIRY_MARGIN_MS = 60_000;
const MILLISECONDS_PER_SECOND = 1_000;
const MAX_CLOCK_DRIFT_ROUND_TRIP_MS = 10_000;

export interface AuthTokenClaims {
  clientId?: string;
  exp?: number;
  iat?: number;
}

interface CachedAuthToken {
  value: string | null;
  expiresAt: number;
}

export interface AuthTokenCache {
  getToken: () => Promise<string | null>;
  invalidate: () => void;
  peekToken: () => string | null;
  refreshToken: (deniedToken: string | null) => Promise<string | null>;
}

export const decodeAuthTokenClaims = (token: string | null): AuthTokenClaims | null => {
  if (isNil(token)) {
    return null;
  }
  try {
    const payloadSegment = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payloadSegment)) as AuthTokenClaims;
  } catch {
    return null;
  }
};

const getDeviceRenewalTime = (token: string, clockDriftMs: number): number | null => {
  const expiresAtSeconds = decodeAuthTokenClaims(token)?.exp;
  return isNil(expiresAtSeconds)
    ? null
    : expiresAtSeconds * MILLISECONDS_PER_SECOND - clockDriftMs - TOKEN_EXPIRY_MARGIN_MS;
};

/** `clockDriftMs` is the server clock minus the device clock. */
export const getTokenCacheExpiry = (token: string, now: number, clockDriftMs: number = 0): number => {
  const renewalTime = getDeviceRenewalTime(token, clockDriftMs);
  if (isNil(renewalTime)) {
    return now + TOKEN_FALLBACK_TTL_MS;
  }
  return Math.max(renewalTime, now + TOKEN_ERROR_TTL_MS);
};

const measureClockDrift = (token: string | null, requestedAt: number, receivedAt: number): number | null => {
  const issuedAtSeconds = decodeAuthTokenClaims(token)?.iat;
  const isRoundTripTooSlow = receivedAt - requestedAt > MAX_CLOCK_DRIFT_ROUND_TRIP_MS;
  if (isNil(issuedAtSeconds) || isRoundTripTooSlow) {
    return null;
  }
  return issuedAtSeconds * MILLISECONDS_PER_SECOND - receivedAt;
};

const isExpiredOnArrival = (token: string, receivedAt: number, clockDriftMs: number): boolean => {
  const renewalTime = getDeviceRenewalTime(token, clockDriftMs);
  return !isNil(renewalTime) && renewalTime <= receivedAt;
};

export const createAuthTokenCache = (fetchToken: () => Promise<string | null>): AuthTokenCache => {
  let cachedToken: CachedAuthToken | null = null;
  let inflightToken: Promise<string | null> | null = null;
  let generation = 0;
  let clockDriftMs = 0;
  let tokenIssuedAfterDenial: string | null = null;

  const getToken = (): Promise<string | null> => {
    if (!isNil(cachedToken) && cachedToken.expiresAt > Date.now()) {
      return Promise.resolve(cachedToken.value);
    }
    if (isNil(inflightToken)) {
      inflightToken = fetchIntoCache(generation);
    }
    return inflightToken;
  };

  const fetchIntoCache = async (requestGeneration: number, tokenExpiredOnArrival?: string): Promise<string | null> => {
    const requestedAt = Date.now();
    const fetchedToken = await fetchToken().catch(() => null);
    const receivedAt = Date.now();
    if (requestGeneration !== generation) {
      return getToken();
    }
    clockDriftMs = measureClockDrift(fetchedToken, requestedAt, receivedAt) ?? clockDriftMs;
    const isFirstAttempt = isNil(tokenExpiredOnArrival);
    if (isFirstAttempt && !isNil(fetchedToken) && isExpiredOnArrival(fetchedToken, receivedAt, clockDriftMs)) {
      return fetchIntoCache(requestGeneration, fetchedToken);
    }
    const token = fetchedToken ?? tokenExpiredOnArrival ?? null;
    cachedToken = {
      value: token,
      expiresAt: isNil(token) ? receivedAt + TOKEN_ERROR_TTL_MS : getTokenCacheExpiry(token, receivedAt, clockDriftMs),
    };
    inflightToken = null;
    return token;
  };

  const invalidate = () => {
    generation += 1;
    cachedToken = null;
    inflightToken = null;
  };

  const peekToken = (): string | null => cachedToken?.value ?? null;

  const refreshToken = async (deniedToken: string | null): Promise<string | null> => {
    if (isNil(deniedToken)) {
      return getToken();
    }
    if (deniedToken === tokenIssuedAfterDenial) {
      return deniedToken;
    }
    if (isNil(inflightToken) && peekToken() === deniedToken) {
      invalidate();
    }
    tokenIssuedAfterDenial = await getToken();
    return tokenIssuedAfterDenial;
  };

  return { getToken, invalidate, peekToken, refreshToken };
};
